import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { getPMFragmentWithFallback } from '../get-pm-fragment-with-fallback';
import { useAnalyticsFlow } from '../../analytics/analytics-flow/analyticsFlowUtils';
import { PreviewScreen } from '@atlassian/generative-ai-modal/screens/Preview';
import { convertPMFragmentToPreview } from '../../ui/convert-markdown-to-preview';
import { useFireAIAnalyticsEvent } from '../../analytics/utils';
import { copyResponseToClipboard } from './utils/copy-response';
import { getHasAcceptableUseWarning } from './utils/get-has-acceptable-use-warning';
import { ErrorUtils } from '../../utils/errors';
import { ProseMirrorContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import { isExperimentEnabled, getExperimentCohort } from '@atlassian/generative-ai-modal/utils/experiments';
import { messages as experienceMessages } from '../messages';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { stripMatchingOuterQuotes } from './utils/strip-matching-outer-quotes';
import { isConfluenceTitleToolbarSuggestTitle } from '../../utils/confluence-suggest-title/is-title-toolbar-suggest-title';
import { createDiffPreview } from '../../ui/create-diff-preview';
import { analyticsFormatMessage } from '../../utils/format-message';
import { isTranslationPrompt } from '../../prebuilt/translations/utils';
export function PreviewScreenWithLogic({
  latestPrompt,
  aupViolation,
  markdown,
  schema,
  editorPluginAIProvider,
  providerFactory,
  editorView,
  positions,
  configItem,
  endExperience,
  isInputActive,
  userInput,
  userADFInput,
  send,
  appearance,
  product,
  lastTriggeredFrom,
  editCount,
  retryPromptCount,
  idMap,
  history,
  contextStatistics,
  backendModel,
  errorInfo,
  promptTrigger
}) {
  var _configItem$disableIn;
  /**
   * Please check with Ethan/PM upon removing onExperienceEvent
   * calls, to confirm it is no longer used in any ongoing experiments.
   */
  useEffect(() => {
    var _editorPluginAIProvid;
    (_editorPluginAIProvid = editorPluginAIProvider.onExperienceEvent) === null || _editorPluginAIProvid === void 0 ? void 0 : _editorPluginAIProvid.REVIEW_STATE_ENTERED();
  }, [editorPluginAIProvider]);
  const {
    formatMessage,
    locale
  } = useIntl();
  const clearInputRef = useRef();
  const lastHistoryEvent = history.entries[history.positionFromEnd];
  const responseTooSimilar = lastHistoryEvent.responseTooSimilar;
  const inputOutputDiffRatio = lastHistoryEvent.inputOutputDiffRatio;

  /**
   * This should not be treated as a fact we are attempting
   * to match as many responses with acceptable use warnings
   * however, we may get false positives or false negatives
   *
   * TODO POST EAP
   * Modify 'getHasAcceptableUseWarning' when the backend is
   * able to return acceptable-use-warning flags to the frontend
   */
  const hasAcceptableUseWarning = getHasAcceptableUseWarning({
    markdown,
    locale
  });
  const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
  const analyticsFlow = useAnalyticsFlow();
  const enqueuedPreviewStep = useRef(false);
  const errorReported = React.useRef(false);
  if (markdown && isConfluenceTitleToolbarSuggestTitle({
    configItem,
    lastTriggeredFrom
  })) {
    markdown = stripMatchingOuterQuotes(markdown);
  }

  // no need to try convert markdown into pmFragment in case of aupViolation
  const pmFragmentPreviewData = aupViolation ? undefined : getPMFragmentWithFallback({
    markdown: markdown || '',
    schema,
    source: 'preview',
    fireAIAnalyticsEvent: event => {
      if (!errorReported.current) {
        errorReported.current = true;
        fireAIAnalyticsEvent(event);
      }
    },
    idMap: idMap
  });
  const pmFragment = (pmFragmentPreviewData === null || pmFragmentPreviewData === void 0 ? void 0 : pmFragmentPreviewData.pmFragment) || Fragment.empty;
  const preview = getPreviewElement(pmFragment);

  /**
   * gets preview JSX element based on fragment
   */
  function getPreviewElement(pmFragment) {
    if (aupViolation) {
      // fire analytics if we get an AUP violation from convoAI
      if (errorInfo && errorInfo.apiName === 'assistance-service') {
        analyticsFlow.enqueueStep({
          stepName: 'apiError',
          attributes: {
            errorType: 'AUP violation detected',
            ...errorInfo
          }
        });
      }
      const aupViolationMessage = formatMessage(experienceMessages.aupViolationMessage, {
        // This is based on guidance at
        // https://developer.atlassian.com/platform/localization/i18n-formatting/avoiding-concatenation/#links
        auplink: chunks => /*#__PURE__*/React.createElement("a", {
          href: "https://www.atlassian.com/legal/acceptable-use-policy",
          target: "_blank"
        }, chunks)
      });
      return /*#__PURE__*/React.createElement("p", null, aupViolationMessage);
    }
    if (responseTooSimilar) {
      const responseTooSimilarMessage = formatMessage(experienceMessages.responseTooSimilarMessage);
      return /*#__PURE__*/React.createElement("p", null, responseTooSimilarMessage);
    }
    return convertPMFragmentToPreview({
      schema,
      pmFragment,
      showTelepointer: false,
      providerFactory
    });
  }
  useEffect(() => {
    analyticsFlow.fireQueuedStep('preview');
    let modelIO;
    if (pmFragmentPreviewData) {
      var _lastHistoryEvent$mod, _localizedLatestPromp;
      const stats = new ProseMirrorContentStatistics(pmFragment);
      const contentStatistics = stats.collectStatistics();
      modelIO = {
        selectionSize: (_lastHistoryEvent$mod = lastHistoryEvent.modelInput.selection) === null || _lastHistoryEvent$mod === void 0 ? void 0 : _lastHistoryEvent$mod.length,
        nodesConvertedToHTMLInput: idMap ? Object.keys(idMap).length : undefined,
        outputHTMLConvertedToNodes: pmFragmentPreviewData.htmlConvertedToNodes,
        outputHTMLConvertedToFallbackNodes: pmFragmentPreviewData.htmlConvertedToFallbackNodes,
        isTextFallbackUsed: pmFragmentPreviewData.isTextFallbackUsed,
        previewNodeCounts: contentStatistics.nodeCounts,
        previewMarkCounts: contentStatistics.markCounts,
        previewTotalCharacterCounts: contentStatistics.totalCharacterCount,
        userInputCharacterCount: (_localizedLatestPromp = localizedLatestPrompt.userInput) === null || _localizedLatestPromp === void 0 ? void 0 : _localizedLatestPromp.length
      };
    }
    analyticsFlow.addAttributes({
      editCount,
      isAupViolation: aupViolation,
      responseTooSimilar,
      modelIO: {
        contextStatistics,
        ...modelIO
      },
      inputOutputDiffRatio
    });
    return () => {
      // We only want to enqueue the preview step if it hasn't been enqueued
      // already. This is to handle the case where the user clicks on an
      // ai action button where we manually enqueue the analytics event,
      // and then seperately fire a complete step.

      if (enqueuedPreviewStep.current === false) {
        analyticsFlow.enqueueStep({
          stepName: 'preview',
          attributes: undefined
        });
      }
    };
    // expecting to enqueue step only once on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isTextColorSupported = ('textColor' in schema.marks);
  const isVisualDiffPreviewEnabled = isTextColorSupported && isExperimentEnabled('editor_ai_-_visual_diff_preview_experiment') && getExperimentCohort('editor_ai_-_visual_diff_preview_experiment') === 'visual-diff-preview';
  const [diffPreview, setDiffPreview] = useState();
  const isTranslations = isTranslationPrompt(latestPrompt);
  const {
    selectionType
  } = configItem;
  useEffect(() => {
    if (isVisualDiffPreviewEnabled && !aupViolation && !responseTooSimilar && pmFragmentPreviewData !== null && pmFragmentPreviewData !== void 0 && pmFragmentPreviewData.pmFragment && !isTranslations) {
      const diff = createDiffPreview({
        editorView,
        pmFragment,
        positions,
        providerFactory
      });
      if (diff) {
        analyticsFlow.addAttributes({
          hasShowDiff: true,
          hasUserHighlightedDifferences: true
        });
        if (selectionType === 'range' && !isTranslationPrompt(latestPrompt)) {
          analyticsFlow.addAttributes({
            hasShowDiffEnabled: true
          });
        }
        setDiffPreview(diff);
      }
    }
  },
  // We only need to calculate the diff once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isVisualDiffPreviewEnabled, analyticsFlow, selectionType, isTranslations]);
  const fireResponseFeedbackEvent = (sentiment, productSuccessState) => {
    fireAIAnalyticsEvent({
      payload: {
        action: 'click',
        actionSubject: 'editorPluginAI',
        actionSubjectId: 'responseFeedbackButton',
        attributes: {
          sentiment,
          hasAcceptableUseWarning,
          appearance,
          product,
          configItemTitle: configItemDefaultTitle,
          lastTriggeredFrom,
          productSuccessStatus: productSuccessState,
          inputOutputDiffRatio,
          retryPromptCount,
          responseTooSimilar,
          aiInteractionID: analyticsFlow.getLastAiInteractionId()
        },
        eventType: 'ui'
      }
    });
  };
  const handleFiringFeedbackEvent = async feedbackMetadata => {
    analyticsFlow.addAttributes({
      userFeedbackSentiment: feedbackMetadata.sentiment
    });

    // handleFeedbackSubmission is intended to be implemented
    // by all products -- there will be a period where products won't have
    // it implemented (and will be relying on THUMBS_UP/THUMBS_DOWN)
    // in these cases -- we don't know the product response status
    if (!editorPluginAIProvider.handleFeedbackSubmission) {
      fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'unknown');
      return;
    } else {
      try {
        const productResponse = await editorPluginAIProvider.handleFeedbackSubmission(feedbackMetadata);
        fireResponseFeedbackEvent(feedbackMetadata.sentiment, productResponse.status);
      } catch {
        fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'failed');
      }
    }
  };

  // preset prompt or base generate / interrogation
  const localizedLatestPrompt = useMemo(() => {
    return latestPrompt !== null && latestPrompt !== void 0 && latestPrompt.presetTitle ? {
      presetTitle: latestPrompt.presetTitle({
        formatMessage
      }),
      userInput: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput,
      userADFInput: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput
    } : {
      userInput: (latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput) || '',
      userADFInput: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput
    };
  }, [latestPrompt, formatMessage]);
  const onEditPromptClick = useCallback(() => {
    const {
      userInput,
      userADFInput,
      presetTitle
    } = localizedLatestPrompt;
    analyticsFlow.addAttributes({
      actionTaken: 'edit-prompt'
    });
    fireAIAnalyticsEvent({
      payload: {
        action: 'click',
        actionSubject: 'editorPluginAI',
        actionSubjectId: 'editPromptButton',
        attributes: {
          experienceType: configItem.title({
            formatMessage: analyticsFormatMessage
          }),
          editCount
        },
        eventType: 'ui'
      }
    });
    send({
      type: 'edit prompt',
      userInput,
      userADFInput,
      presetTitle
    });
  }, [localizedLatestPrompt, send, editCount, configItem, fireAIAnalyticsEvent, analyticsFlow]);
  const onShowDiffPreview = useCallback(enabled => {
    analyticsFlow.addAttributes({
      hasUserHighlightedDifferences: enabled
    });
  }, [analyticsFlow]);
  const configItemDefaultTitle = useMemo(() => configItem.title({
    formatMessage: analyticsFormatMessage
  }), [configItem]);
  const aiExperienceMetadata = {
    hasAcceptableUseWarning,
    configItemTitle: configItemDefaultTitle,
    lastTriggeredFrom,
    inputOutputDiffRatio,
    backendModel
  };
  const getAIExperience = hasUserConsent => ({
    ...aiExperienceMetadata,
    ...(hasUserConsent && {
      userPrompt: localizedLatestPrompt.userInput,
      markdownResponse: markdown
    })
  });
  React.useEffect(() => {
    /**
     * When userInput is empty, we need to clear interrogate editor
     *  as setting empty userInput as defaultValue won't clear editor.
     */
    if (!userInput) {
      var _clearInputRef$curren;
      (_clearInputRef$curren = clearInputRef.current) === null || _clearInputRef$curren === void 0 ? void 0 : _clearInputRef$curren.call(clearInputRef);
    }
  }, [userInput]);
  const onInputChange = React.useCallback(inputValue => {
    send({
      type: 'update user input',
      input: inputValue
    });
  }, [send]);
  const onADFChange = React.useCallback(inputValue => {
    send({
      type: 'update user input adf',
      adf: inputValue
    });
  }, [send]);
  const disableInterrogation = ((_configItem$disableIn = configItem.disableInterrogation) === null || _configItem$disableIn === void 0 ? void 0 : _configItem$disableIn.call(configItem)) || editorPluginAIProvider.disableInterrogation;

  // if convoAI related FF is enabled
  const convoAIBeta = getBooleanFF('platform.editor.ai-assistance-service-free-generate') || getBooleanFF('platform.editor.ai-interrogate-with-convo-ai');

  // if it is generate or interrogate
  const isGenerateOrInterrogatePrompt = promptTrigger && ['interrogate', 'generate'].includes(promptTrigger);
  let additionalFooterText;
  if (isGenerateOrInterrogatePrompt && convoAIBeta && latestPrompt !== null && latestPrompt !== void 0 && latestPrompt.userInput) {
    // if the prompt includes a link
    const containsURL = latestPrompt.userInput.includes('http://') || latestPrompt.userInput.includes('https://');
    if (containsURL) {
      additionalFooterText = formatMessage(experienceMessages.convoAIBeta);
    }
  }
  return /*#__PURE__*/React.createElement(PreviewScreen, {
    latestPrompt: localizedLatestPrompt,
    diffPreview: diffPreview,
    canShowDiffPreview: selectionType === 'range' && !isTranslations,
    preview: preview,
    disableActions: aupViolation || !!responseTooSimilar,
    inputValue: userInput,
    inputADFValue: userADFInput,
    isInputActive: isInputActive
    // Remove passing product prop while cleaning up platform.editor.ai-remove-beta-lozenge-jpd_1t8rt FF.
    ,
    product: editorPluginAIProvider.product,
    onCancel: () => {
      if (isInputActive) {
        send({
          type: 'cancel refinement'
        });
      } else {
        send({
          type: 'enable discard'
        });
      }
    },
    onInputChange: onInputChange,
    onADFChange: onADFChange,
    onInputFocus: () => {
      send({
        type: 'start refinement'
      });
    },
    onInputBlur: () => {
      if (!userInput) {
        send({
          type: 'cancel refinement'
        });
      }
    },
    onSubmit: () => {
      /**
       * Do we really need to pass userInput and userADFInput
       *  on firing interrogate event.
       * We should have latest userInput and userADFInput
       *  set by onInputChange and onADFChange.
       */
      send({
        type: 'interrogate',
        userInput: userInput,
        userADFInput,
        latestPromptResponse: markdown
      });
    },
    onCopy: () => {
      fireAIAnalyticsEvent({
        payload: {
          action: 'click',
          actionSubject: 'editorPluginAI',
          actionSubjectId: 'copyButton',
          attributes: {
            experienceType: configItem.title({
              formatMessage: analyticsFormatMessage
            }),
            editCount
          },
          eventType: 'ui'
        }
      });
      analyticsFlow.addAttributes({
        contentCopied: true
      });
      copyResponseToClipboard({
        editorView,
        pmFragment
      });
    },
    onRetry: () => {
      fireAIAnalyticsEvent({
        payload: {
          action: 'click',
          actionSubject: 'editorPluginAI',
          actionSubjectId: 'retryPromptButton',
          attributes: {
            experienceType: configItemDefaultTitle
          },
          eventType: 'ui'
        }
      });
      send({
        type: 'retry prompt',
        input: localizedLatestPrompt.userInput,
        adf: localizedLatestPrompt.userADFInput
      });
    },
    onGoodResponseButtonClick: () => {
      const feedbackMetadata = {
        sentiment: 'good',
        getAIExperience,
        editorAttributes: {
          appearance,
          product
        }
      };

      // Now deprecated - migrate to handleFeedbackSubmission
      editorPluginAIProvider.onExperienceEvent && editorPluginAIProvider.onExperienceEvent.THUMBS_UP();
      handleFiringFeedbackEvent(feedbackMetadata);
    },
    onBadResponseButtonClick: () => {
      const feedbackMetadata = {
        sentiment: 'bad',
        getAIExperience,
        editorAttributes: {
          appearance,
          product
        }
      };

      // Now deprecated - migrate to handleFeedbackSubmission
      editorPluginAIProvider.onExperienceEvent && editorPluginAIProvider.onExperienceEvent.THUMBS_DOWN();
      handleFiringFeedbackEvent(feedbackMetadata);
    },
    onEditPromptClick: onEditPromptClick,
    onShowDiffPreview: onShowDiffPreview,
    applications: configItem.actions.map(unformattedAction => {
      const action = unformattedAction({
        formatMessage
      });
      return {
        label: action.title,
        appearance: action.appearance,
        onClick: () => {
          try {
            var _editorPluginAIProvid2;
            analyticsFlow.addAttributes({
              actionTaken: action.analyticsTitle
            });
            analyticsFlow.enqueueStep({
              stepName: 'preview',
              attributes: undefined
            });
            // We set this to true to prevent the preview step from being
            // enqueued again on unmount
            // Note -- in future -- we expect the complete step to be deprecated
            // an replaced with a "content highlighted" step
            enqueuedPreviewStep.current = true;
            analyticsFlow.fireQueuedStep('complete');
            const beforeActionRun = performance.now();
            action.run({
              editorView,
              positions,
              markdown: markdown,
              pmFragment
            });
            analyticsFlow.enqueueStep({
              stepName: 'complete',
              attributes: {
                actionTaken: action.analyticsTitle,
                duration: Math.round(performance.now() - beforeActionRun),
                editCount: editCount,
                retryPromptCount,
                inputOutputDiffRatio
              }
            });

            /**
             * Please check with Ethan/PM upon removing onExperienceEvent
             * calls, to confirm it is no longer used in any ongoing experiments.
             */
            (_editorPluginAIProvid2 = editorPluginAIProvider.onExperienceEvent) === null || _editorPluginAIProvid2 === void 0 ? void 0 : _editorPluginAIProvid2.EXPERIENCE_COMPLETE();
            endExperience();
          } catch (error) {
            fireAIAnalyticsEvent({
              payload: {
                action: 'unhandledErrorCaught',
                actionSubject: 'editorPluginAI',
                actionSubjectId: 'experienceApplication',
                attributes: {
                  errorType: 'actionRunError',
                  actionTitle: action.analyticsTitle,
                  errorMessage: ErrorUtils.extractErrorMessage(error)
                },
                eventType: 'operational'
              }
            });
            send({
              type: 'unknown fail',
              failureReason: 'unknown fail',
              errorInfo: {}
            });
          }
        },
        id: 'insert-button'
      };
    }),
    PromptEditor: editorPluginAIProvider.PromptEditor,
    clearInputRef: clearInputRef,
    additionalFooterText: additionalFooterText,
    providerFactory: providerFactory,
    schema: schema,
    disableInterrogation: disableInterrogation
  });
}